import {
  authCapturePayment,
  getPaymentSettings,
  cancelPayment,
} from '../../api/api'
import { all, takeEvery, call, put, select } from 'redux-saga/effects'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga, {
  handleResponseError,
} from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { requestLedger } from 'portal-modules/ledger/redux/modules/ledger'
import { addPaymentMethod } from './paymentMethods'
import { localStorage as local } from '@entrata/utils'
import { JWT_STORAGE_KEY } from 'portal-modules/common/constants/keys'
import { addCustomContent } from 'portal-modules/common/redux/modules/customContent'
import { hideLoader } from 'portal-modules/common/redux/modules/toggleSuccessModals'
import { getSessionCookies } from 'portal-utils/session-cookies'

// action types
const MAKE_A_PAYMENT = 'rp/payments/MAKE_A_PAYMENT'
const POSTED_PAYMENT = 'rp/payments/POSTED_PAYMENT'
const REQUEST_PAYMENT_SETTINGS = 'rp/payments/REQUEST_PAYMENT_SETTINGS'
const CANCEL_PAYMENT = 'rp/payments/CANCEL_PAYMENT'

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case POSTED_PAYMENT:
      return payload
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// action creators
export const makeAPayment = createFormAction(MAKE_A_PAYMENT)

export const paymentCancel = createFormAction(CANCEL_PAYMENT)

export const requestPaymentSettings = () => ({
  type: REQUEST_PAYMENT_SETTINGS,
})

// sagas
function* postPayment({ payload, type, callback }) {
  const response = yield call(authCapturePayment, payload)
  if (payload.save_to_wallet) {
    const walletInfo = {
      payload: {
        agrees_to_terms: payload.agrees_to_terms,
        [payload.card_info
          ? 'card_info'
          : payload.bank_info
          ? 'bank_info'
          : 'sepa_info']: payload.card_info
          ? payload.card_info
          : payload.bank_info
          ? payload.bank_info
          : payload.sepa_info,
        nickname: payload.nickname,
        payment_type_id: payload.payment_type_id,
        save_to_wallet: payload.save_to_wallet,
      },
    }
    yield put(addPaymentMethod(walletInfo))
  }

  if (typeof response === 'string' && response.startsWith('<pre>')) {
    throw new Error(__('Payment failed'))
  }

  const {
    payments,
    requires_3dsecure,
    threed_secure_iframe_url,
    use_3ds_flex,
    issuer_url,
    secure_postback,
    md_data,
    challenge_token,
  } = response

  const rp_at = getSessionCookies(JWT_STORAGE_KEY)

  const additionalVerificationDetails = {
    requires3dSecure: requires_3dsecure,
    iframeUrl: `${threed_secure_iframe_url}&is_react_dev=${process.env
      .NODE_ENV === 'development'}&auth_token=${rp_at}`,
    use3dsFlex: use_3ds_flex,
    issuerUrl: issuer_url,
    securePostBack: `${secure_postback}&is_react_dev=${process.env.NODE_ENV ===
      'development'}`,
    mdData: md_data,
    challengeToken: challenge_token,
  }

  if (payments.length && type === MAKE_A_PAYMENT) {
    let actions = []

    actions.push({
      type: POSTED_PAYMENT,
      payload: {
        ...response,
        paymentAccountId: payload?.customer_payment_account_id,
      },
    })

    actions.push(requestLedger())

    const {
      settings: { payments },
    } = yield select()

    if (payments.is_duplicate_payment) {
      actions.push(
        addSettings({ payments: { ...payments, is_duplicate_payment: false } }),
      )
    }

    yield all(actions.map(a => put(a)))
  }

  if (requires_3dsecure) {
    callback(payments[0].id, additionalVerificationDetails)
  } else {
    callback(payments[0].id)
  }
}

function* handlePaymentError(err) {
  if (err.code == 3133 || err.code == 3134) {
    const {
      settings: { payments },
    } = yield select()

    yield put(
      addSettings({
        payments: { ...payments, is_duplicate_payment: true },
      }),
    )
  }

  yield call(handleResponseError, err, { key: 'payments' })
}

function* fetchPaymentSettings() {
  const {
    settings: { payments },
  } = yield select()

  const paymentSettings = payments ? payments : yield call(getPaymentSettings)

  yield put(
    addSettings({
      payments: { ...paymentSettings, is_duplicate_payment: false },
    }),
  )
  yield put(addCustomContent({ payments: paymentSettings.custom_text }))
}

function* watchNewPayment() {
  yield takeEvery(
    MAKE_A_PAYMENT,
    createRequestSaga(postPayment, {
      key: 'payments',
      message: __('Processing payment'),
      successMessage: __('Your payment was successful!'),
      onError: handlePaymentError,
    }),
  )
}

function* watchRequestPaymentSettings() {
  yield takeEvery(
    REQUEST_PAYMENT_SETTINGS,
    createRequestSaga(fetchPaymentSettings, {
      key: 'payments',
      messgae: __('Looking up payment settings'),
      duration: 500,
    }),
  )
}

function* postPaymentCancel({ payload, callback }) {
  const response = yield call(cancelPayment, payload)
  yield put(hideLoader())
  if (response.code === 200) {
    typeof callback === 'function' && callback(true)
  } else {
    typeof callback === 'function' && callback(false)
    throw new Error(__(response.message))
  }
}

// watchers
function* watchPaymentCancel() {
  yield takeEvery(
    CANCEL_PAYMENT,
    createRequestSaga(postPaymentCancel, {
      key: 'payments',
      message: __('Cancelling Payment'),
      onError: handleCancelPaymentError,
    }),
  )
}

function* handleCancelPaymentError(err) {
  yield call(handleResponseError, err, { key: 'payments' })
}

export const sagas = [
  watchNewPayment,
  watchRequestPaymentSettings,
  watchPaymentCancel,
]
